import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { useReducedMotion } from '../../hooks/useReducedMotion';

const clamp = (value: number): number => Math.max(0, Math.min(value, 100));

export const DataRing = ({
  value,
  label,
  size = 94,
  ringColor = 'var(--color-brand-lime)',
  bootSequence = false,
  active = true,
  startDelay = 0,
  countDuration = 1.2
}: {
  value: number;
  label: string;
  size?: number;
  ringColor?: string;
  bootSequence?: boolean;
  active?: boolean;
  startDelay?: number;
  countDuration?: number;
}) => {
  const reducedMotion = useReducedMotion();
  const mobileViewport = typeof window !== 'undefined' && window.matchMedia('(max-width: 960px)').matches;
  const normalized = clamp(value);
  const strokeWidth = 8;
  const radius = size / 2 - strokeWidth;
  const circumference = 2 * Math.PI * radius;
  const targetOffset = circumference * (1 - normalized / 100);
  const shouldBootAnimate = bootSequence && !reducedMotion;
  const [displayValue, setDisplayValue] = useState(shouldBootAnimate && !active ? 0 : normalized);

  useEffect(() => {
    if (!shouldBootAnimate) {
      setDisplayValue(normalized);
      return;
    }

    if (!active) {
      setDisplayValue(0);
      return;
    }

    let raf = 0;
    let startedAt = 0;
    const timeout = window.setTimeout(() => {
      const tick = (timestamp: number) => {
        if (!startedAt) {
          startedAt = timestamp;
        }

        const progress = Math.min((timestamp - startedAt) / (countDuration * 1000), 1);
        setDisplayValue(Math.round(normalized * progress));

        if (progress < 1) {
          raf = window.requestAnimationFrame(tick);
        }
      };

      raf = window.requestAnimationFrame(tick);
    }, startDelay * 1000);

    return () => {
      window.clearTimeout(timeout);
      if (raf) {
        window.cancelAnimationFrame(raf);
      }
    };
  }, [active, countDuration, normalized, shouldBootAnimate, startDelay]);

  return (
    <div className="group flex flex-col items-center gap-2">
      <div className="relative">
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label={`${label} ${normalized}%`}>
          <circle cx={size / 2} cy={size / 2} r={radius} fill="transparent" stroke="rgba(183,255,106,0.15)" strokeWidth={strokeWidth} />
          <motion.circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="transparent"
            stroke={ringColor}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={circumference}
            initial={shouldBootAnimate || !mobileViewport ? { strokeDashoffset: circumference } : false}
            animate={
              shouldBootAnimate
                ? { strokeDashoffset: active ? targetOffset : circumference }
                : mobileViewport
                  ? { strokeDashoffset: targetOffset }
                  : undefined
            }
            whileInView={shouldBootAnimate || mobileViewport ? undefined : { strokeDashoffset: targetOffset }}
            viewport={shouldBootAnimate || mobileViewport ? undefined : { once: true, amount: 0.6 }}
            transition={{
              duration: 1.2,
              delay: shouldBootAnimate ? startDelay : 0,
              ease: [0.2, 1, 0.32, 1]
            }}
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
          />
          <motion.circle
            cx={size / 2}
            cy={size / 2}
            r={radius + 3}
            fill="transparent"
            stroke="rgba(88,247,182,0.35)"
            strokeWidth="1"
            animate={reducedMotion ? undefined : { opacity: [0.25, 0.8, 0.25] }}
            transition={{ duration: 2.2, repeat: Infinity }}
          />
          <motion.circle
            cx={size / 2}
            cy={size / 2}
            r={radius + 8}
            fill="transparent"
            stroke="rgba(183,255,106,0.45)"
            strokeDasharray="2 7"
            strokeWidth="1"
            animate={reducedMotion ? undefined : { rotate: 360 }}
            transition={{ duration: 8.5, ease: 'linear', repeat: Infinity }}
            style={{ transformOrigin: '50% 50%' }}
          />
        </svg>
        <motion.span
          className="pointer-events-none absolute left-1/2 top-1/2 h-1.5 w-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-brand-lime"
          animate={reducedMotion ? undefined : { opacity: [0.35, 1, 0.45], scale: [0.7, 1.2, 0.9] }}
          transition={{ duration: 1.8, repeat: Infinity }}
        />
      </div>
      <div className="text-center">
        <motion.p
          className="text-lg font-semibold text-brand-text"
          animate={reducedMotion ? undefined : { opacity: [0.8, 1, 0.8] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          {(shouldBootAnimate ? displayValue : normalized)}%
        </motion.p>
        <p className="hud-micro text-brand-text-3">{label}</p>
      </div>
    </div>
  );
};
